// MissionPreview component
// Visual preview of how the mission will appear to operators
// CHANGE: Created for mission creation wizard step 4 - Phase 2 Enhancement
// CHANGE: Redesigned to match operator interface (TaskExecutionView) for accurate preview
// Shows all mission details in operator-friendly format with recurrence info
// CHANGE: Changed background from gray (bg-gray-50) to white with border for better visual separation from modal footer

import { Clock, Repeat } from 'lucide-react';
import { formatDuration } from '../../lib/dateUtils';

interface TaskItemData {
  name: string;
  description: string;
  has_free_field: boolean;
  free_field_label: string | null;
  free_field_mandatory: boolean;
  order_index: number;
}

interface RecurrenceData {
  frequency: 'daily' | 'weekly' | 'monthly' | 'yearly';
  interval: number;
  daysOfWeek: number[];
  endMode: 'never' | 'date' | 'occurrences';
  endDate: string | null;
  endOccurrences: number;
}

interface MissionPreviewProps {
  name: string;
  description?: string;
  durationMinutes: number;
  serviceName?: string;
  tasks: TaskItemData[];
  recurrence?: RecurrenceData;
}

export function MissionPreview({
  name,
  description,
  durationMinutes,
  serviceName,
  tasks,
  recurrence
}: MissionPreviewProps) {
  // Generate recurrence description
  const getRecurrenceDescription = (): string => {
    if (!recurrence) return '';

    const { frequency, interval, daysOfWeek } = recurrence;

    switch (frequency) {
      case 'daily':
        return interval === 1 ? 'Tous les jours' : `Tous les ${interval} jours`;

      case 'weekly': {
        if (daysOfWeek.length === 0) return 'Hebdomadaire';
        const dayNames = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
        const selectedDays = daysOfWeek
          .sort((a, b) => a - b)
          .map(day => dayNames[day - 1])
          .map(day => day.charAt(0).toUpperCase() + day.slice(1));

        if (interval === 1) {
          return `Tous les ${selectedDays.join(', ')}`;
        }
        return `Toutes les ${interval} semaines le ${selectedDays.join(', ')}`;
      }

      case 'monthly':
        return interval === 1 ? 'Tous les mois' : `Tous les ${interval} mois`;

      case 'yearly':
        return interval === 1 ? 'Tous les ans' : `Tous les ${interval} ans`;

      default:
        return '';
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
      {/* Header Section - Operator style */}
      <div className="bg-white border-b border-gray-200 rounded-t-lg p-6">
        <h1 className="text-xl font-bold text-gray-900 mb-2">
          {name}
        </h1>
        <div className="flex items-center gap-3 text-sm text-gray-600 mb-3">
          <Clock size={14} className="text-gray-400" />
          <span>Durée : {formatDuration(durationMinutes)}</span>
        </div>
        {serviceName && (
          <div className="inline-flex items-center px-3 py-1 bg-primary-600 text-white text-xs font-medium rounded-full">
            {serviceName}
          </div>
        )}
      </div>

      {/* Description section */}
      {description && description.trim() && (
        <div className="mx-6 mt-4 px-4 border border-blue-200 rounded-lg bg-blue-50 py-3">
          <div className="text-sm text-blue-900 leading-relaxed whitespace-pre-wrap">
            {description}
          </div>
        </div>
      )}

      {/* Tasks Section - Operator style */}
      <div className="p-6 pt-4">
        <div className="space-y-3">
          {tasks.map((task, index) => (
            <div
              key={index}
              className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
            >
              <h4 className="font-medium text-gray-900 text-base mb-1">
                {task.name}
              </h4>

              {task.description && (
                <p className="text-sm text-gray-600 mt-1">
                  {task.description}
                </p>
              )}

              {/* Text input field - like TaskItemRow */}
              {task.has_free_field && (
                <div className="mt-3">
                  {task.free_field_mandatory && (
                    <p className="text-xs text-gray-500 mb-2">
                      Obligatoire
                    </p>
                  )}
                  <input
                    type="text"
                    readOnly
                    disabled
                    placeholder={task.free_field_label || ''}
                    className="w-full px-4 py-3 border rounded-lg transition-all text-base bg-gray-50 text-gray-700 border-gray-200 cursor-default"
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Recurrence Section - Subtle style */}
      {recurrence && (
        <div className="mx-6 mb-6 bg-gray-100 border border-gray-300 rounded-lg p-5">
          <div className="flex items-start gap-3">
            <Repeat size={18} className="text-gray-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-base font-semibold text-gray-900 mb-1">
                Mission récurrente
              </h3>
              <p className="text-gray-700 text-sm font-medium mb-2">
                {getRecurrenceDescription()}
              </p>

              {/* End condition */}
              <div className="text-sm text-gray-600">
                {recurrence.endMode === 'never' && 'Se répète indéfiniment'}
                {recurrence.endMode === 'date' && recurrence.endDate && (
                  <>Jusqu'au {new Date(recurrence.endDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</>
                )}
                {recurrence.endMode === 'occurrences' && (
                  <>Limité à {recurrence.endOccurrences} {recurrence.endOccurrences === 1 ? 'occurrence' : 'occurrences'}</>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
